import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDate } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";
import { useMarkSeen, SeenStatusLine } from "../components/SeenIndicator";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = { planning: "Planung", in_progress: "In Arbeit", on_hold: "Pausiert", completed: "Abgeschlossen" };
const MS_STATUS = { pending: "Offen", in_progress: "In Arbeit", done: "Abgeschlossen" };

/**
 * Feb 2026 · 11 Sep 2026 · Persistent Zweizeiler mit Haupt-Concierge +
 * Verantwortlichem — analog zum `BookingHeaderAssigneeLines` in
 * `BookingDetailModal.jsx`. Zeigt beide Rollen immer, auch wenn identisch.
 * User-Directive: „Zweizeiler auch in Projekt-Detail spiegeln — konsistente
 * UX über Buchungen und Projekte."
 */
function ProjectHeaderAssigneeLines({ project }) {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/members");
        setMembers((data || []).filter((u) => ["admin", "concierge"].includes(u.role)));
      } catch { /* silent */ }
    })();
  }, []);
  const nameOf = (id) => {
    if (!id) return null;
    const m = members.find((x) => x.id === id);
    return m?.name || m?.email || null;
  };
  const hauptId = project?.assigned_concierge_id || null;
  const respId = project?.assigned_to || null;
  const hauptName = nameOf(hauptId);
  const respName = nameOf(respId);
  const bothSameId = hauptId && respId && hauptId === respId;
  return (
    <div className="mt-2 flex flex-col gap-0.5" data-testid="project-header-assignee-lines">
      <div className="text-[11.5px] leading-tight" data-testid="project-header-haupt-concierge">
        <span className="tracking-[0.14em] uppercase text-navy-dark/50 mr-1">Haupt-Concierge · </span>
        {hauptName ? (
          <span className="text-navy-dark/85">{hauptName}</span>
        ) : (
          <span className="text-navy-dark/45 italic">— (nicht zugewiesen)</span>
        )}
      </div>
      <div className="text-[11.5px] leading-tight" data-testid="project-header-responsible">
        <span className="tracking-[0.14em] uppercase text-navy-dark/50 mr-1">Verantwortlich · </span>
        {respName ? (
          <span className="text-navy-dark/85">
            {respName}
            {bothSameId && (
              <span className="ml-1.5 text-[10px] tracking-[0.14em] uppercase text-navy-dark/40"
                    data-testid="project-header-responsible-same-as-haupt">
                (identisch)
              </span>
            )}
          </span>
        ) : hauptName ? (
          <span className="text-navy-dark/70">
            {hauptName}
            <span className="ml-1.5 text-[10px] tracking-[0.14em] uppercase text-navy-dark/40"
                  data-testid="project-header-responsible-via-property">
              (via Objekt)
            </span>
          </span>
        ) : (
          <span className="text-navy-dark/45 italic">— (nicht zugewiesen)</span>
        )}
      </div>
    </div>
  );
}

export default function TeamProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const seenNow = useMarkSeen("project", id, true);
  const [newMs, setNewMs] = useState({ title: "", description: "", due_date: "" });
  const [uploadingMs, setUploadingMs] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}`); setP(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addMs = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/milestones`, { ...newMs, due_date: newMs.due_date || null });
      toast.success("Meilenstein hinzugefügt.");
      setNewMs({ title: "", description: "", due_date: "" });
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  const updateMs = async (mid, patch) => {
    try {
      await api.patch(`/projects/${id}/milestones/${mid}`, patch);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  const uploadPhoto = async (mid, file) => {
    setUploadingMs(mid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${id}/milestones/${mid}/photos?file_id=${data.id}`);
      toast.success("Foto hinzugefügt.");
      await load();
    } catch (err) { toast.error("Upload fehlgeschlagen."); }
    finally { setUploadingMs(null); }
  };

  const setStatus = (mid, status) => updateMs(mid, { status });

  const projectStatus = async (status) => {
    try { await api.patch(`/projects/${id}`, { status }); await load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  if (!p) return <div className="max-w-[1200px] mx-auto p-16 text-center label-caps">Wird geladen…</div>;

  const total = p.milestones?.length || 0;
  const done = p.milestones?.filter((m) => m.status === "done").length || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="project-detail">
      <Link to="/team/projects" className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold">← Alle Projekte</Link>
      <div className="mt-6 mb-8">
        <div className="label-caps mb-3">{p.property_name} · {STATUS_LABEL[p.status] || p.status}</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">{p.title}</h1>
        <ProjectHeaderAssigneeLines project={p} />
        <div className="mt-2"><SeenStatusLine entity={{ ...(p || {}), ...(seenNow || {}) }} testid="project-seen-status" /></div>
        {p.description && <p className="text-[15px] text-navy-dark/80 mt-3 max-w-3xl leading-relaxed">{p.description}</p>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 pt-6 border-t border-navy/10">
          <div>
            <div className="label-caps mb-1">Zeitleiste</div>
            <div className="text-[13px] text-navy">
              {p.start_date ? deDate(p.start_date) : "—"}<br />
              → {p.end_date ? deDate(p.end_date) : "—"}
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Budget</div>
            <div className="font-display text-xl text-navy">{p.budget ? deCurrency(p.budget, p.currency || "EUR") : "—"}</div>
          </div>
          <div>
            <div className="label-caps mb-1">Fortschritt</div>
            <div className="font-display text-xl text-navy">{done}/{total} · {pct}%</div>
            <div className="h-1 w-full bg-navy/10 mt-2"><div className="h-1" style={{ width: `${pct}%`, background: BRAND_COLORS.gold }} /></div>
          </div>
          <div>
            <div className="label-caps mb-1">Status ändern</div>
            <select data-testid="project-status-select" value={p.status} onChange={(e) => projectStatus(e.target.value)}
              className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] w-full">
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-10">
        <h2 className="font-display text-2xl text-navy mb-6">Meilensteine</h2>
        <div className="relative pl-6 border-l-2 border-navy/10">
          {(p.milestones || []).map((m, i) => (
            <div key={m.id} data-testid={`milestone-${m.id}`} className="relative mb-8 pb-6">
              <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full"
                style={{ background: m.status === "done" ? BRAND_COLORS.greenDeep : m.status === "in_progress" ? BRAND_COLORS.gold : BRAND_COLORS.ivory, border: "2px solid #0F1E3D" }} />
              <div className="bg-white border border-navy/10 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="label-caps mb-1">Schritt {i + 1} · {MS_STATUS[m.status] || m.status}{m.due_date ? " · Fällig " + deDate(m.due_date) : ""}</div>
                    <div className="font-display text-lg text-navy">{m.title}</div>
                    {m.description && <p className="text-[13px] text-navy-dark/70 mt-1">{m.description}</p>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["pending", "in_progress", "done"].map((s) => (
                      <button key={s} data-testid={`ms-${m.id}-status-${s}`} onClick={() => setStatus(m.id, s)}
                        className={`px-3 py-1 text-[10.5px] tracking-[0.15em] uppercase border ${
                          m.status === s
                            ? s === "done" ? "bg-green-700 text-ivory border-green-700"
                            : s === "in_progress" ? "bg-gold text-navy border-gold"
                            : "bg-navy text-ivory border-navy"
                            : "border-navy/20 text-navy-dark/60 hover:border-navy/40"
                        }`}>
                        {MS_STATUS[s]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 cursor-pointer border border-navy/20 px-3 py-1.5 text-[11px] tracking-widest uppercase hover:border-gold">
                    <input type="file" accept="image/*" className="hidden"
                      data-testid={`ms-${m.id}-upload`}
                      onChange={(e) => e.target.files?.[0] && uploadPhoto(m.id, e.target.files[0])} />
                    📷 Foto hinzufügen {uploadingMs === m.id && "…"}
                  </label>
                  {(m.photo_file_ids || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {m.photo_file_ids.map((fid) => (
                        <img key={fid} data-testid={`ms-${m.id}-photo`} alt="Fortschritt"
                          src={`${API_BASE}/api/files/${fid}`}
                          className="w-16 h-16 object-cover border border-navy/10" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {(!p.milestones || !p.milestones.length) && (
            <div className="text-[13px] text-navy-dark/50">Noch keine Meilensteine.</div>
          )}
        </div>
      </div>

      <form onSubmit={addMs} data-testid="add-milestone-form"
        className="bg-white border border-navy/10 p-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input required data-testid="ms-title-input" placeholder="Neuer Meilenstein" value={newMs.title}
          onChange={(e) => setNewMs({ ...newMs, title: e.target.value })}
          className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]" />
        <DatePicker testid="ms-due-input" placeholder="Fällig am"
          value={newMs.due_date} onChange={(v) => setNewMs({ ...newMs, due_date: v })} />
        <input placeholder="Beschreibung (optional)" value={newMs.description}
          onChange={(e) => setNewMs({ ...newMs, description: e.target.value })}
          className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]" />
        <button data-testid="ms-add-btn" type="submit"
          className="text-ivory py-2 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
          style={{ background: BRAND_COLORS.navy }}>+ Meilenstein</button>
      </form>
    </div>
  );
}
